import React, { useEffect, useState } from 'react';
import { TOURNAMENT_QUICK_CREATE_PAGE } from 'constants/pages';
import { VAIR_MINIMUM_PLAYERS } from 'constants/vair';
import {
  EventGroupFormatsEnum,
  PaymentFulfillmentChannelsEnum,
  SportsEnum,
  useGetEventDetailsLazyQuery,
} from 'types/generated/client';
import { getEngagementSourceFromUrl, trackView } from 'services/client/analytics/engagement';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { EventExperience, getEventExperience } from 'utils/shared/events/getEventExperience';
import { getActivityImageUrl } from 'utils/shared/getActivityImageUrl';
import { pluralize } from 'utils/shared/pluralize';
import { isIndexableEventForSeo } from 'utils/shared/seo/isIndexableEventForSeo';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import VictoryCupSimple from 'svg/VictoryCupSimple';
import BankAccountPrompt from 'screens/CoachDetails/BankAccountPrompt';
import SafeAreaPage from 'layouts/SafeAreaPage';
import { Button, ButtonLink, ButtonText } from 'components/Button';
import Footer from 'components/Footer';
import Modal, { useModal } from 'components/modals/Modal';
import Head from 'components/utilities/Head';
import CreateTournamentCard from './CreateTournamentCard';
import GamedayCountdown from './Register/GamedayCountdown';
import ModalVairInfo from './Register/ModalVairInfo';
import Register from './Register/Register';
import TournamentContent from './TournamentContent';
import { EXIT_TEXT, Event, PageProps } from './types';

const SIGNUP_MODAL_TIMEOUT = 5_000;
const IS_SIGNUP_MODAL_OPEN_ENABLED = true;

const SPORT_LABELS: Record<SportsEnum, string> = {
  [SportsEnum.Pickleball]: 'Pickleball',
  [SportsEnum.Tennis]: 'Tennis',
  [SportsEnum.Padel]: 'Padel',
  [SportsEnum.PlatformTennis]: 'Platform Tennis',
};

function getEventMetaTitle(title: string, experience: EventExperience): string {
  // Intentionally omit the sport — keep the share/OG title generic (e.g. "… | Social").
  return experience.label ? `${title} | ${experience.label}` : title;
}

function getEventMetaDescription(
  event: {
    title: string;
    sport: SportsEnum;
    description: string;
    city?: { name: string; countrySubdivision: { code: string } } | null;
    displayLocation: string;
    addressString?: string | null;
  },
  experience: EventExperience,
): string {
  // The catch-all type has no copy of its own.
  if (!experience.label) return event.description;
  const sportLabel = (SPORT_LABELS[event.sport] ?? 'Pickleball').toLowerCase();
  const location = event.city
    ? `${event.city.name}, ${event.city.countrySubdivision.code}`
    : event.displayLocation || event.addressString;
  return `${experience.joinVerb} the ${event.title} ${sportLabel} ${experience.noun}. Being played in ${location}. Find ${sportLabel} ${experience.nounPlural} on Bounce.`;
}

export default function TournamentDetails(props: PageProps) {
  const {
    isOpen: isRegistrationOpen,
    openModal: openRegistrationModal,
    closeModal: closeRegistrationModal,
  } = useModal();
  const {
    isOpen: isVairInfoModalOpen,
    openModal: openVairInfoModal,
    closeModal: closeVairInfoModal,
  } = useModal();
  const [isShowGamedayCountdown, setIsShowGamedayCountdown] = useState(false);
  const { openSignupModal, ModalLogin, ModalSignup } = useAuthModals();
  const { isAnonymousSession, userId } = useViewer();
  const [getEventDetailsLazyQuery, { data }] = useGetEventDetailsLazyQuery();

  const staticPageEvent = props.event;

  useEffect(() => {
    /**
     * @note typically we would wait for the user's firebase to sync, but anonymous and logged in user should have the same permissions
     */
    if (staticPageEvent?.id && !staticPageEvent.isExternal) {
      getEventDetailsLazyQuery({
        variables: {
          id: staticPageEvent.id,
        },
      });
    }
  }, [staticPageEvent?.id]);

  useEffect(() => {
    if (isAnonymousSession && staticPageEvent?.isExternal) {
      setTimeout(() => {
        if (IS_SIGNUP_MODAL_OPEN_ENABLED) {
          openSignupModal();
        }
      }, SIGNUP_MODAL_TIMEOUT);
    }
  }, [isAnonymousSession]);

  // Engagement: record a detail-page view for this event (socials/leagues/tournaments).
  // Source is attributed from the `?src=` param set by the referring surface.
  const eventId = data?.eventsByPk?.id || staticPageEvent?.id;
  useEffect(() => {
    if (!eventId) return;
    trackView({ entityType: 'EVENT', entityId: eventId, source: getEngagementSourceFromUrl() });
  }, [eventId]);

  if (!staticPageEvent) {
    return null;
  }

  const { jsonLd } = props;

  const decoratedStaticPageEvent: Event = {
    ...staticPageEvent,
    registrations: [],
    groups: [],
    sponsors: [],
    registrationsAggregate: { aggregate: { count: 0 } },
    isQuickCreate: data?.eventsByPk?.isQuickCreate || false,
    paymentFulfillmentChannel:
      data?.eventsByPk?.paymentFulfillmentChannel ?? PaymentFulfillmentChannelsEnum.OnPlatform,
  };
  const event = data?.eventsByPk || decoratedStaticPageEvent;
  const isUserOrganizer = event?.hostUserProfile?.id === userId;
  const experience = getEventExperience(event);
  const isCanceled = !!event?.canceledAt;
  const shouldNoIndex = !isIndexableEventForSeo(event);

  const isVairRoundRobin = event.groups?.some(
    (group) => group.format === EventGroupFormatsEnum.VairRoundRobin,
  );
  const registeredPlayerCount = event?.registrationsAggregate?.aggregate?.count || 0;

  const handleGameDayClick = () => {
    if (isUserOrganizer && isVairRoundRobin && registeredPlayerCount < VAIR_MINIMUM_PLAYERS) {
      openVairInfoModal();
      return;
    }
    setIsShowGamedayCountdown(true);
  };

  if (!event) {
    return null;
  }

  return (
    <>
      <Head
        title={getEventMetaTitle(event.title, experience)}
        description={getEventMetaDescription(event, experience)}
        ogImage={
          event.coverImageUrl
            ? getImageUrl({
                url: event.coverImageUrl,
                path: event.coverImagePath || '',
              })
            : getActivityImageUrl(event) || undefined
        }
        canonicalType="currentUrlStripQuery"
        noIndex={shouldNoIndex}
      />
      <SafeAreaPage isShowTopNav isHideSidebar>
        <main className="mx-auto h-full w-full max-w-[70rem] bg-color-bg-lightmode-primary pb-4 dark:bg-color-bg-darkmode-primary lg:pb-20">
          <div className="flex h-full w-full lg:flex-row-reverse lg:pt-8">
            <div className="sticky top-24 hidden h-full w-full self-start bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary lg:block lg:max-w-[26rem]">
              {isShowGamedayCountdown && !isRegistrationOpen ? (
                <GamedayCountdown event={event} onGoBack={() => setIsShowGamedayCountdown(false)} />
              ) : (
                <>
                  <Register
                    event={event}
                    isUserOrganizer={isUserOrganizer}
                    onGameDayClick={handleGameDayClick}
                    onEventDataChanged={() => {
                      getEventDetailsLazyQuery({
                        variables: { id: staticPageEvent.id },
                        fetchPolicy: 'network-only',
                      });
                    }}
                  />
                  {event?.isExternal && (
                    <div className="mt-8">
                      <CreateTournamentCard />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="w-full flex-1 lg:pr-10">
              <TournamentContent
                event={event}
                faqs={props.faqs}
                jsonLd={props.jsonLd}
                isUserOrganizer={isUserOrganizer}
              />
            </div>
          </div>
        </main>
        <Footer
          isBottomRegister={event.isExternal}
          ignoreText={EXIT_TEXT}
          ignoreAction={() => {
            window.open(event.externalUrl || '', '_blank');
          }}
          onSubmitSignup={() => {
            window.open(event.externalUrl || '', '_blank');
          }}
        />
        <div>
          <div className="flex w-full items-center justify-center border-t border-transparent p-4 opacity-0 lg:hidden">
            <div className="mx-auto w-full max-w-lg">
              <Button
                variant="brand"
                size="lg"
                onClick={() => openRegistrationModal()}
                className="w-full"
              >
                Register
              </Button>
            </div>
          </div>
          <div className="max-w-auto fixed bottom-24 mx-auto flex w-full flex-row items-center justify-center p-4 lg:hidden">
            {isCanceled ? null : isUserOrganizer && experience.hasCompetition ? (
              <>
                <div className="mx-auto flex w-full max-w-lg flex-row gap-4">
                  <div className="w-full">
                    <Button variant="inverted" size="lg" onClick={() => openRegistrationModal()}>
                      Register
                    </Button>
                  </div>
                  <div className="w-full">
                    <Button
                      variant="brand"
                      size="lg"
                      onClick={() => {
                        handleGameDayClick();
                        openRegistrationModal();
                      }}
                    >
                      Gameday
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex w-full items-center justify-center border-t border-transparent lg:hidden">
                <div className="mx-auto w-full max-w-lg">
                  <Button
                    variant="brand"
                    size="lg"
                    onClick={() => openRegistrationModal()}
                    className="w-full"
                  >
                    Register
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </SafeAreaPage>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      <Modal
        classNameRounded="rounded-3xl"
        isOpen={isRegistrationOpen}
        handleClose={closeRegistrationModal}
      >
        <ButtonText
          onClick={() => closeRegistrationModal()}
          className="absolute right-4 top-4 rounded-full p-2"
        >
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
        {isShowGamedayCountdown ? (
          <GamedayCountdown
            event={event}
            onGoBack={() => {
              closeRegistrationModal();
              setTimeout(() => {
                setIsShowGamedayCountdown(false);
              }, 300);
            }}
          />
        ) : (
          <Register
            event={event}
            isUserOrganizer={isUserOrganizer}
            onGameDayClick={handleGameDayClick}
            onEventDataChanged={() => {
              getEventDetailsLazyQuery({
                variables: { id: staticPageEvent.id },
                fetchPolicy: 'network-only',
              });
            }}
          />
        )}
        {event?.isExternal && (
          <div className="mb-5 border-t border-color-border-input-lightmode p-4 dark:border-color-border-input-darkmode">
            <ButtonLink
              variant="brand"
              size="lg"
              label="Create Round-robin"
              iconLeft={<VictoryCupSimple className="h-5 w-5" />}
              href={TOURNAMENT_QUICK_CREATE_PAGE}
              isExternal
            />
          </div>
        )}
      </Modal>
      <ModalLogin isShowSignupLink />
      <ModalSignup isShowLoginLink />
      <ModalVairInfo
        isOpen={isVairInfoModalOpen}
        onClose={closeVairInfoModal}
        title="Minimum Players Required"
        message={`Four players are required before starting Gameday. Currently there ${pluralize({
          count: registeredPlayerCount,
          singular: 'is',
          plural: 'are',
          hideCount: true,
        })} ${pluralize({
          count: registeredPlayerCount,
          singular: 'player',
          plural: 'players',
        })} registered.`}
      />
    </>
  );
}
